import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Slider,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { createKeyword, updateKeyword, fetchKeywordById } from '../../store/slices/aiInstructionSlice';
import { AIInstructionFormData } from '../../types/aiInstruction';

interface KeywordFormProps {
  open: boolean;
  keywordId: number | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const KeywordForm: React.FC<KeywordFormProps> = ({
  open,
  keywordId,
  onClose,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentKeyword, loading } = useSelector((state: RootState) => state.aiInstructions);

  const [formData, setFormData] = useState<AIInstructionFormData>({
    keyword: '',
    aiInstructions: '',
    isActive: true,
    caseSensitive: false,
    matchWholeWord: false,
    priority: 50,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loadingKeyword, setLoadingKeyword] = useState(false);

  useEffect(() => {
    if (open && keywordId) {
      // Edit mode - load keyword
      setLoadingKeyword(true);
      dispatch(fetchKeywordById(keywordId))
        .unwrap()
        .then((keyword) => {
          setFormData({
            keyword: keyword.keyword,
            aiInstructions: keyword.aiInstructions,
            isActive: keyword.isActive,
            caseSensitive: keyword.caseSensitive,
            matchWholeWord: keyword.matchWholeWord,
            priority: keyword.priority,
          });
          setLoadingKeyword(false);
        })
        .catch(() => {
          onError('Nepodařilo se načíst klíčové slovo');
          setLoadingKeyword(false);
          onClose();
        });
    } else if (open && !keywordId) {
      // Create mode - reset form
      setFormData({
        keyword: '',
        aiInstructions: '',
        isActive: true,
        caseSensitive: false,
        matchWholeWord: false,
        priority: 50,
      });
      setErrors({});
    }
  }, [open, keywordId, dispatch, onClose, onError]);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.keyword.trim()) {
      newErrors.keyword = 'Klíčové slovo je povinné';
    } else if (formData.keyword.length > 255) {
      newErrors.keyword = 'Klíčové slovo nesmí být delší než 255 znaků';
    }

    if (!formData.aiInstructions.trim()) {
      newErrors.aiInstructions = 'AI instrukce jsou povinné';
    } else if (formData.aiInstructions.length < 10) {
      newErrors.aiInstructions = 'AI instrukce musí mít alespoň 10 znaků';
    } else if (formData.aiInstructions.length > 5000) {
      newErrors.aiInstructions = 'AI instrukce nesmí být delší než 5000 znaků';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (keywordId) {
        // Update existing keyword
        await dispatch(updateKeyword({ id: keywordId, data: formData })).unwrap();
        onSuccess('Klíčové slovo bylo úspěšně upraveno');
      } else {
        // Create new keyword
        await dispatch(createKeyword(formData)).unwrap();
        onSuccess('Klíčové slovo bylo úspěšně vytvořeno');
      }
    } catch (error: any) {
      onError(error || 'Nepodařilo se uložit klíčové slovo');
    }
  };

  const handleChange = (field: keyof AIInstructionFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {keywordId ? 'Upravit klíčové slovo' : 'Přidat nové klíčové slovo'}
      </DialogTitle>

      <DialogContent>
        {loadingKeyword ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {/* Keyword */}
            <TextField
              fullWidth
              label="Klíčové slovo *"
              value={formData.keyword}
              onChange={(e) => handleChange('keyword', e.target.value)}
              error={!!errors.keyword}
              helperText={errors.keyword || 'Text, který se bude vyhledávat ve fakturách'}
              placeholder='např. "Novák s.r.o.", "IČO: 1234567890", "123456789/0800"'
              sx={{ mb: 2 }}
            />

            {/* AI Instructions */}
            <TextField
              fullWidth
              label="AI Instrukce *"
              value={formData.aiInstructions}
              onChange={(e) => handleChange('aiInstructions', e.target.value)}
              error={!!errors.aiInstructions}
              helperText={
                errors.aiInstructions ||
                `Instrukce pro AI zpracování (${formData.aiInstructions.length}/5000)`
              }
              placeholder="Příklad:
- Číslo účtu je 123456789/0800
- Extrahovat variabilní symbol z pole číslo faktury"
              multiline
              rows={6}
              sx={{ mb: 2 }}
            />

            {/* Active Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
              label="Aktivní"
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> Neaktivní klíčová slova se nebudou vyhledávat, ale zůstanou
                uložena pro pozdější použití.
              </Typography>
            </Alert>

            {/* Case Sensitive */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.caseSensitive}
                  onChange={(e) => handleChange('caseSensitive', e.target.checked)}
                />
              }
              label="Rozlišovat velikost písmen"
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, ml: 4 }}>
              Pokud zapnuto, "Novák" se nebude shodovat s "novák"
            </Typography>

            {/* Match Whole Word */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.matchWholeWord}
                  onChange={(e) => handleChange('matchWholeWord', e.target.checked)}
                />
              }
              label="Hledat pouze celé slovo"
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, ml: 4 }}>
              Pokud zapnuto, "Novák" se nebude shodovat s "Nováková" nebo "Novákovi"
            </Typography>

            {/* Priority Slider */}
            <Typography gutterBottom>
              Priorita: {formData.priority}
            </Typography>
            <Slider
              value={formData.priority}
              onChange={(_, value) => handleChange('priority', value as number)}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
              valueLabelDisplay="auto"
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Vyšší priorita (75-100) přepisuje nižší, pokud se shoduje více klíčových slov
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Zrušit
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || loadingKeyword}
        >
          {loading ? <CircularProgress size={24} /> : keywordId ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeywordForm;

